import { useEffect } from 'react';

import { MarketStatus } from '@/types/api.generated';

import { createPlayerEventKey } from '../utils/markets-cache-store-utils';
import { allPlayerPropsSelector, usePlayerPropsStore } from './use-player-props-store';

/**
 * Randomly updates the player projections every 5 seconds for easier testing of live updates and suspending markets
 */
export const useDebugSimulateLiveMarketsUpdates = ({
    enabled,
    enableRandomMarketSuspension,
    changeAllMarkets,
    playerId,
    eventId,
}: {
    enabled: boolean;
    /**
     * Enables random market suspension and resume
     */
    enableRandomMarketSuspension: boolean;
    /**
     * Will cause that at each 5sec step, all markets to be changed. Otherwise, only 5 random markets will be updated.
     */
    changeAllMarkets: boolean;
    playerId: string;
    eventId: string;
}) => {
    const updateStorePlayersProjections = usePlayerPropsStore(state => state.actions.updateStorePlayersProjections);
    const playerMarkets = usePlayerPropsStore(allPlayerPropsSelector(playerId, eventId));

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const updateRandomMarkets = () => {
            // Select 3 random indices to change 5 projections
            const indicesToUpdateValues = new Set<number>();
            while (indicesToUpdateValues.size < 5 && indicesToUpdateValues.size < playerMarkets.length) {
                indicesToUpdateValues.add(Math.floor(Math.random() * playerMarkets.length));
            }
            if (changeAllMarkets) {
                for (let i = 0; i < playerMarkets.length; i++) {
                    indicesToUpdateValues.add(i);
                }
            }

            // Select 2 random indices to SUSPEND | UNSUSPEND markets
            const indicesToUpdateMarketStatus = new Set<number>();
            while (indicesToUpdateMarketStatus.size < 2 && indicesToUpdateMarketStatus.size < playerMarkets.length) {
                indicesToUpdateMarketStatus.add(Math.floor(Math.random() * playerMarkets.length));
            }

            // Create a copy of all markets and modify only selected ones
            const allProjections = playerMarkets.map((projection, index) => {
                if (indicesToUpdateMarketStatus.has(index) && enableRandomMarketSuspension) {
                    return {
                        ...projection,
                        marketStatus:
                            projection.marketStatus === MarketStatus.Suspended
                                ? MarketStatus.Opened
                                : MarketStatus.Suspended,
                    };
                }
                if (indicesToUpdateValues.has(index)) {
                    let newCurrentValue: number | undefined;
                    const randomChange = (Math.random() - 0.5) * 5;
                    if (projection.currentValue) {
                        newCurrentValue = Math.max(projection.currentValue + randomChange, 0);
                    }
                    const newValue = Math.max(projection.value + randomChange, 0);
                    let newNonRegularValue: number | undefined;
                    if (projection.nonRegularValue) {
                        newNonRegularValue = Math.max(projection.nonRegularValue + randomChange, 0);
                    }
                    return {
                        ...projection,
                        value: newValue,
                        currentValue: newCurrentValue,
                        nonRegularValue: newNonRegularValue,
                    };
                }
                return projection;
            });

            const newMarkets = {
                [createPlayerEventKey(playerId, eventId)]: allProjections,
            };

            updateStorePlayersProjections(newMarkets);
        };

        const interval = setInterval(updateRandomMarkets, 5000);

        return () => clearInterval(interval);
    }, [
        changeAllMarkets,
        enableRandomMarketSuspension,
        enabled,
        eventId,
        playerId,
        playerMarkets,
        updateStorePlayersProjections,
    ]);
};
